import { useState, useRef } from 'react';
import Image from 'next/image';

interface MediaItem {
  type: "image" | "video";
  src: string;
  alt: string;
  width: number;
  height: number;
  mobileWidth: number;
  mobileHeight: number;
  className: string;
}

interface ScrollingMediaProps {
  mediaItems: MediaItem[];
  className?: string;
}

export default function ScrollingMedia({ 
  mediaItems, 
  className = ""
}: ScrollingMediaProps) {
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    const wrapper = wrapperRef.current;
    if (!container || !wrapper) return;
    
    wrapper.style.animationPlayState = 'paused';
    container.scrollBy({ left: -400, behavior: 'smooth' });
    
    setTimeout(() => {
      if (wrapper) wrapper.style.animationPlayState = 'running';
    }, 500);
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    const wrapper = wrapperRef.current;
    if (!container || !wrapper) return;
    
    wrapper.style.animationPlayState = 'paused';
    container.scrollBy({ left: 400, behavior: 'smooth' });
    
    setTimeout(() => {
      if (wrapper) wrapper.style.animationPlayState = 'running';
    }, 500);
  };

  const handleMediaClick = (media: MediaItem) => {
    setSelectedMedia(media);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMedia(null);
  };

  const renderMedia = (media: MediaItem, index: number, suffix: string = '') => (
    <div
      key={`media-${index}${suffix}`}
      className="flex-shrink-0 cursor-pointer media-item"
      onClick={() => handleMediaClick(media)}
      style={{
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)'
      }}
    >
      <div className="hover:scale-105 transition-transform duration-300">
        {media.type === "image" ? (
          <Image
            src={media.src}
            alt={media.alt}
            width={media.width}
            height={media.height}
            className={media.className}
            style={{
              display: 'block'
            }}
            loading="lazy"
          />
        ) : (
          <video
            src={media.src}
            width={media.width}
            height={media.height}
            className={media.className}
            style={{
              display: 'block'
            }}
            controls={false}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <style jsx>{`
        @keyframes scrollLoop {
          0% {
            transform: translate3d(0, 0, 0);
          }
          100% {
            transform: translate3d(-50%, 0, 0);
          }
        }
        
        .scroll-container {
          animation: scrollLoop 20s linear infinite;
          will-change: transform;
          backface-visibility: hidden;
          transform: translateZ(0);
        }
        
        .media-item img,
        .media-item video {
          width: ${mediaItems[0]?.mobileWidth || 325}px;
          height: ${mediaItems[0]?.mobileHeight || 550}px;
        }
        
        @media (min-width: 768px) {
          .scroll-container {
            animation-duration: 40s;
          }
          .media-item:nth-child(odd) img,
          .media-item:nth-child(odd) video {
            width: ${mediaItems[0]?.width || 325}px !important;
            height: ${mediaItems[0]?.height || 550}px !important;
          }
          .media-item:nth-child(even) img,
          .media-item:nth-child(even) video {
            width: ${mediaItems[1]?.width || 335}px !important;
            height: ${mediaItems[1]?.height || 482}px !important;
          }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .scroll-container {
            animation: none;
          }
        }
        
        .scroll-wrapper {
          contain: layout style paint;
          isolation: isolate;
        }
        
        .scroll-wrapper::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <section 
        className={`py-12 relative ${className}`}
        style={{ background: 'linear-gradient(180deg, #E8F5F7 50%, white 50%)' }}
      >
        <button
          onClick={scrollLeft}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-all hover:scale-110"
          aria-label="Scrolla a sinistra"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          onClick={scrollRight}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-all hover:scale-110"
          aria-label="Scrolla a destra"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div 
          ref={scrollContainerRef}
          className="w-full overflow-x-auto overflow-y-hidden scroll-wrapper"
          style={{
            scrollBehavior: 'smooth',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          <div 
            ref={wrapperRef}
            className="relative w-[200%] flex scroll-container gap-8 items-center"
          >
            {mediaItems.map((media, index) => renderMedia(media, index))}
            {mediaItems.map((media, index) => renderMedia(media, index, '-second'))}
          </div>
        </div>

        {isModalOpen && selectedMedia && (
          <div
            className="fixed inset-0 z-[10000] bg-black bg-opacity-80 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <div
              className={`relative max-h-full rounded-2xl overflow-hidden shadow-2xl ${
                selectedMedia.type === "video"
                  ? "max-w-sm sm:max-w-md bg-black"
                  : "max-w-4xl bg-white"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 bg-white bg-opacity-90 text-black rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-100 transition-all shadow-lg"
                aria-label="Chiudi"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {selectedMedia.type === "image" ? (
                <Image
                  src={selectedMedia.src}
                  alt={selectedMedia.alt}
                  width={800}
                  height={600}
                  className="w-full h-auto max-h-[90vh] object-contain"
                />
              ) : (
                <video
                  src={selectedMedia.src}
                  className="w-full h-auto max-h-[90vh] object-contain"
                  controls
                  autoPlay
                  playsInline
                />
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
